import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import {
  HighlightTransforms,
  HighlightTranslatable,
  HighlightTranslation,
} from '@go-shared/types/translation';
import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeHighlightTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandEventHighlight } from '@go-shared/types/brand';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';

interface HighlightsTranslationDataBundle {
  highlights: BrandEventHighlight[];
  highlightTranslations: HighlightTranslation[];
  highlightsMap: { [key: string]: BrandEventHighlight };
  highlightTranslationsMap: { [key: string]: HighlightTranslation };
}

interface HighlightTranslationForm {
  dirty: boolean;
  transforms: HighlightTransforms;
}

interface ChangeHighlightFormAction {
  active?: boolean;
  valueChange?: { key: keyof HighlightTranslatable; value: string };
}

type HighlightTranslationsPageProps = RouteComponentProps;

export default function HighlightTranslations(props: HighlightTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<HighlightsTranslationDataBundle | undefined>(
    undefined
  );
  const [activeHighlight, setActiveHighlight] = useState<BrandEventHighlight | undefined>(
    undefined
  );
  const [highlightTranslationForm, setHighlightTranslationForm] = useState<
    HighlightTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();

  const serverInfo = useServerInfo();
  const { brand, languages } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active highlight if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveHighlight(state => {
      const brandEventHighlight = dataBundle.highlights[0];
      if (!state) return brandEventHighlight;
      const refreshedHighlight = dataBundle.highlightsMap[state._id];
      return refreshedHighlight || brandEventHighlight;
    });
  }, [dataBundle]);

  // this will reset the form each time a new highlight is clicked
  useEffect(() => {
    resetHighlightsForm();
  }, [activeHighlight]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.highlights, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredHighlights = !dataBundle ? [] : fuseSearchResults || dataBundle.highlights;

  async function fetchDataBundle() {
    try {
      const highlights = brand.eventHighlights;
      const highlightTranslations = await getTranslations('highlight');

      const highlightsMap = mapBy(highlights, '_id');
      const highlightTranslationsMap = mapBy(highlightTranslations, 'refers');

      setDataBundle({
        highlights: highlights,
        highlightTranslations: highlightTranslations,
        highlightsMap: highlightsMap,
        highlightTranslationsMap: highlightTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetHighlightsForm() {
    const transforms = activeHighlight
      ? makeHighlightTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.highlightTranslationsMap[activeHighlight._id]
        )
      : undefined;

    if (!transforms) {
      setHighlightTranslationForm(undefined);
      return;
    }

    const formData: HighlightTranslationForm = {
      dirty: false,
      transforms,
    };

    setHighlightTranslationForm(formData);
  }

  function changeHighlightForm(lang: string, change: ChangeHighlightFormAction) {
    if (!highlightTranslationForm) return;

    const updatedForm = { ...highlightTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setHighlightTranslationForm(updatedForm);
  }

  async function handleSubmitHighlightForm() {
    if (
      !highlightTranslationForm ||
      !activeHighlight ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    )
      return;

    const translation = generateDefaultNewTranslation(activeHighlight._id, 'highlight');

    translation.transforms = highlightTranslationForm.transforms;

    await replaceTranslationByRefer(activeHighlight._id, translation);
    await fetchDataBundle();
    setHighlightTranslationForm({ ...highlightTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="highlights" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredHighlights.map(highlight => (
            <div
              key={highlight._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeHighlight && safeIdCompare(activeHighlight._id, highlight._id) ? 'active' : ''
              }`}
              onClick={() => setActiveHighlight(highlight)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{highlight.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredHighlights.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No highlights found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeHighlight && highlightTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This highlight cannot be modified unless you're in a brand instance and a brand
                  admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeHighlight.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({highlightTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !highlightTranslationForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeHighlightForm(language.key, {
                          active: !highlightTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        highlightTranslationForm.transforms[language.key].active
                          ? 'eye'
                          : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !highlightTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={highlightTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeHighlightForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitHighlightForm()}
                variant="navy"
                disabled={
                  !highlightTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!highlightTranslationForm.dirty}
                inverse
                onClick={() => resetHighlightsForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (highlightTranslationForm && highlightTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={highlightTranslationForm && highlightTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="highlights"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
